import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  PathsApiUserAssetsGetParametersQueryAssetSource,
  PathsApiUserAssetsGetParametersQueryAssetType,
  UserCollectionRequestType,
} from "@apis/generated/openapi";
import {
  fetchCollectionList,
  fetchProductList,
  fetchUserProductList,
  getUserAssetsV2,
  type CharacterAssetRecord,
  type UserAssetV2Record,
  type UserProductRecord,
} from "@apis/http/api";
import { useScrollLoad, type ScrollPageResult, type UseScrollLoadReturn } from "@hooks/useScrollLoad";
import type { Status } from "@models/common";
import type { CharacterType, FocusType, ProductType, VideoType } from "../librarySlice";
import { LIBRARY_IMAGE_ASSETS_REFRESH_EVENT } from "../events";
import { characterTypeToCategory, fetchCharactersPage, type CharacterListParams } from "./characterListFetcher";
import { fetchGeneratedVideoAssetsPage, GENERATED_VIDEO_ASSETS_REFRESH_EVENT } from "./useGeneratedVideoAssets";

const PAGE_SIZE = 20;

export interface UseLibraryCardsOptions {
  focusType: FocusType;
  videoType: VideoType;
  productType: ProductType;
  characterType: CharacterType;
  enabled?: boolean;
}

export interface UseLibraryCardsResult {
  items: any[];
  status: Status;
  loadingMore: boolean;
  hasMore: boolean;
  sentinelRef: (node: HTMLElement | null) => void;
  refresh: () => void;
  removeItem: (predicate: (item: any) => boolean) => void;
  generatedItems: UserAssetV2Record[];
  generatedRefresh: () => void;
  generatedRemoveItem: (predicate: (item: UserAssetV2Record) => boolean) => void;
}

interface StaticListState<T> {
  items: T[];
  loading: boolean;
  error: Error | null;
}

const noopSentinelRef = () => {};

const getStatus = ({ items, loading, error }: { items: unknown[]; loading: boolean; error: Error | null }): Status => {
  if (loading) return "loading";
  if (error) return "failed";
  return items.length > 0 ? "succeeded" : "blank";
};

const createAssetFetcher =
  (
    assetType: PathsApiUserAssetsGetParametersQueryAssetType,
    assetSource?: PathsApiUserAssetsGetParametersQueryAssetSource,
  ) =>
  async ({ pageNo, pageSize }: { pageNo: number; pageSize: number }): Promise<ScrollPageResult<UserAssetV2Record>> => {
    const response = await getUserAssetsV2({
      assetType,
      assetSource,
      pageNo,
      pageSize,
    });
    const data = (response?.data ?? []).filter((record) => record.state === undefined || record.state === 1);

    return {
      data,
      pageNo: Number(response?.pageNo ?? pageNo),
      pageSize: Number(response?.pageSize ?? pageSize),
      total: Number(response?.total ?? data.length),
    };
  };

const fetchUploadedAssets = createAssetFetcher(
  PathsApiUserAssetsGetParametersQueryAssetType.VID,
  PathsApiUserAssetsGetParametersQueryAssetSource.USER,
);
const fetchImageAssets = createAssetFetcher(PathsApiUserAssetsGetParametersQueryAssetType.IMG);

const fetchSavedVideos = async () => {
  return (await fetchCollectionList({ type: UserCollectionRequestType.Value1 } as any)) as unknown as any[];
};

const fetchScripts = async () => {
  return (await fetchCollectionList({ type: UserCollectionRequestType.Value2 } as any)) as unknown as any[];
};

const fetchProducts = async () => {
  return (await fetchProductList()) as unknown as any[];
};

const fetchCreatedProductsPage = async ({
  pageNo,
  pageSize,
}: {
  pageNo: number;
  pageSize: number;
}): Promise<ScrollPageResult<UserProductRecord>> => {
  const response = await fetchUserProductList({ pageNo, pageSize });
  const data = response?.data ?? [];
  return {
    data,
    pageNo: Number(response?.pageNo ?? pageNo),
    pageSize: Number(response?.pageSize ?? pageSize),
    total: Number(response?.total ?? data.length),
  };
};

const useStaticLibraryList = <T>(enabled: boolean, fetcher: () => Promise<T[]>) => {
  const requestSeqRef = useRef(0);
  const [state, setState] = useState<StaticListState<T>>({
    items: [],
    loading: false,
    error: null,
  });

  const load = useCallback(async () => {
    const requestSeq = requestSeqRef.current + 1;
    requestSeqRef.current = requestSeq;
    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const items = await fetcher();
      if (requestSeqRef.current !== requestSeq) return;
      setState({
        items: Array.isArray(items) ? items : [],
        loading: false,
        error: null,
      });
    } catch (error) {
      if (requestSeqRef.current !== requestSeq) return;
      setState((prev) => ({
        ...prev,
        loading: false,
        error: error instanceof Error ? error : new Error(String(error)),
      }));
    }
  }, [fetcher]);

  useEffect(() => {
    if (!enabled) return;
    void load();
  }, [enabled, load]);

  const removeItem = useCallback((predicate: (item: T) => boolean) => {
    setState((prev) => ({
      ...prev,
      items: prev.items.filter((item) => !predicate(item)),
    }));
  }, []);

  return {
    ...state,
    refresh: load,
    removeItem,
  };
};

const toAssetResult = (result: UseScrollLoadReturn<UserAssetV2Record>) => ({
  items: result.items,
  status: getStatus({
    items: result.items,
    loading: result.loading,
    error: result.error,
  }),
  loadingMore: result.loadingMore,
  hasMore: result.hasMore,
  sentinelRef: result.sentinelRef,
  refresh: result.refresh,
  removeItem: result.removeItem,
});

export const useLibraryCards = ({
  focusType,
  videoType,
  productType,
  characterType,
  enabled = true,
}: UseLibraryCardsOptions): UseLibraryCardsResult => {
  const { pathname } = useLocation();
  const prevPathnameRef = useRef(pathname);
  const generated = useScrollLoad<UserAssetV2Record, Record<string, never>>({
    enabled,
    params: {},
    pageSize: PAGE_SIZE,
    fetcher: fetchGeneratedVideoAssetsPage,
  });

  useEffect(() => {
    const handleRefresh = () => {
      generated.refresh();
    };
    window.addEventListener(GENERATED_VIDEO_ASSETS_REFRESH_EVENT, handleRefresh);
    return () => window.removeEventListener(GENERATED_VIDEO_ASSETS_REFRESH_EVENT, handleRefresh);
  }, [generated.refresh]);

  useEffect(() => {
    if (pathname === "/library" && prevPathnameRef.current !== "/library") {
      generated.refresh();
    }
    prevPathnameRef.current = pathname;
  }, [generated.refresh, pathname]);

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState !== "visible" || pathname !== "/library") return;
      generated.refresh();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => document.removeEventListener("visibilitychange", onVisibilityChange);
  }, [generated.refresh, pathname]);
  const uploaded = useScrollLoad<UserAssetV2Record, Record<string, never>>({
    enabled: enabled && focusType === "video" && videoType === "uploaded",
    params: {},
    pageSize: PAGE_SIZE,
    fetcher: fetchUploadedAssets,
  });
  const images = useScrollLoad<UserAssetV2Record, Record<string, never>>({
    enabled: enabled && focusType === "image",
    params: {},
    pageSize: PAGE_SIZE,
    fetcher: fetchImageAssets,
  });

  useEffect(() => {
    const handleRefresh = () => {
      images.refresh();
    };
    window.addEventListener(LIBRARY_IMAGE_ASSETS_REFRESH_EVENT, handleRefresh);
    return () => window.removeEventListener(LIBRARY_IMAGE_ASSETS_REFRESH_EVENT, handleRefresh);
  }, [images.refresh]);

  const savedVideos = useStaticLibraryList(enabled && focusType === "video" && videoType === "saved", fetchSavedVideos);
  const scripts = useStaticLibraryList(enabled && focusType === "script", fetchScripts);
  const savedProducts = useStaticLibraryList(
    enabled && focusType === "product" && productType === "saved",
    fetchProducts,
  );
  const createdProducts = useScrollLoad<UserProductRecord, Record<string, never>>({
    enabled: enabled && focusType === "product" && productType === "created",
    params: {},
    pageSize: PAGE_SIZE,
    fetcher: fetchCreatedProductsPage,
  });

  const charactersCategory = characterTypeToCategory[characterType];
  const characterScrollParams = useMemo(
    (): CharacterListParams => ({ category: charactersCategory }),
    [charactersCategory],
  );
  const characters = useScrollLoad<CharacterAssetRecord, CharacterListParams>({
    enabled: enabled && focusType === "character",
    params: characterScrollParams,
    pageSize: PAGE_SIZE,
    fetcher: fetchCharactersPage,
  });

  return useMemo(() => {
    if (focusType === "video" && videoType === "generated") {
      return {
        ...toAssetResult(generated),
        generatedItems: generated.items,
        generatedRefresh: generated.refresh,
        generatedRemoveItem: generated.removeItem,
      };
    }

    if (focusType === "video" && videoType === "uploaded") {
      return {
        ...toAssetResult(uploaded),
        generatedItems: generated.items,
        generatedRefresh: generated.refresh,
        generatedRemoveItem: generated.removeItem,
      };
    }

    if (focusType === "image") {
      return {
        ...toAssetResult(images),
        generatedItems: generated.items,
        generatedRefresh: generated.refresh,
        generatedRemoveItem: generated.removeItem,
      };
    }

    if (focusType === "product" && productType === "created") {
      return {
        ...toAssetResult(createdProducts as unknown as UseScrollLoadReturn<UserAssetV2Record>),
        generatedItems: generated.items,
        generatedRefresh: generated.refresh,
        generatedRemoveItem: generated.removeItem,
      };
    }

    if (focusType === "character") {
      return {
        ...toAssetResult(characters as unknown as UseScrollLoadReturn<UserAssetV2Record>),
        generatedItems: generated.items,
        generatedRefresh: generated.refresh,
        generatedRemoveItem: generated.removeItem,
      };
    }

    const staticResult = focusType === "video" ? savedVideos : focusType === "script" ? scripts : savedProducts;

    return {
      items: staticResult.items,
      status: getStatus({
        items: staticResult.items,
        loading: staticResult.loading,
        error: staticResult.error,
      }),
      loadingMore: false,
      hasMore: false,
      sentinelRef: noopSentinelRef,
      refresh: staticResult.refresh,
      removeItem: staticResult.removeItem,
      generatedItems: generated.items,
      generatedRefresh: generated.refresh,
      generatedRemoveItem: generated.removeItem,
    };
  }, [
    focusType,
    productType,
    characters,
    generated,
    images,
    savedProducts,
    createdProducts,
    savedVideos,
    scripts,
    uploaded,
    videoType,
  ]);
};
